import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import type { Lead } from '@/domains/types'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'
import { LeadRow } from './LeadRow'

export interface GroupShape {
  investigation: { id: string; name: string; description: string; imageUrl?: string | null }
  leads: Lead[]
}

interface InvestigationAccordionProps {
  group: GroupShape
  open: boolean
  onToggle: () => void
  openLeads: Set<string>
  onToggleLead: (id: string) => void
  isActive: boolean
  onSetActive: () => void
  onSetActiveLead: (leadId: string | null) => void
  activeLeadId: string | null
  activeFactId?: string | null
}

/**
 * Single investigation accordion row: header with title, image, lead count
 * and active-toggle, plus an expandable list of {@link LeadRow}s.
 */
export function InvestigationAccordion({
  group,
  open,
  onToggle,
  openLeads,
  onToggleLead,
  isActive,
  onSetActive,
  onSetActiveLead,
  activeLeadId,
  activeFactId,
}: InvestigationAccordionProps) {
  const { t } = useTranslation()
  const { investigation, leads } = group

  // Bug 1 fix: only resolve activeLeadId for the active investigation.
  // For inactive investigations, always return null (no lead should be marked as active).
  const resolvedActiveLeadId = isActive && activeLeadId ? activeLeadId : null

  // Bug 2 fix: sort leads so the active lead appears first
  const sortedLeads = useMemo(() => {
    const leadsCopy = [...leads]
    if (resolvedActiveLeadId) {
      return leadsCopy.sort((leadA, leadB) => {
        if (leadA.id === resolvedActiveLeadId) return -1
        if (leadB.id === resolvedActiveLeadId) return 1
        return 0
      })
    }
    return leadsCopy
  }, [leads, resolvedActiveLeadId])

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 bg-card hover:bg-muted/50 transition-colors text-left"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 transition-transform" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 transition-transform" />
        )}

        {investigation.imageUrl && (
          <img
            src={investigation.imageUrl}
            alt=""
            className="w-8 h-8 rounded object-cover shrink-0"
          />
        )}

        <div className="flex-1 min-w-0">
          <span className="font-semibold text-foreground">{investigation.name}</span>
          {investigation.description && (
            <span className="text-sm text-muted-foreground ml-2 hidden sm:inline">
              — {markdownToPlainExcerpt(investigation.description, 70)}
            </span>
          )}
        </div>

        <Badge variant="secondary" className="shrink-0">
          {t('admin.live_topic_leads', { count: leads.length })}
        </Badge>

        {!isActive && (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 text-xs h-7 px-2"
            onClick={(event) => {
              event.stopPropagation()
              onSetActive()
            }}
          >
            {t('admin.live_set_active_investigation')}
          </Button>
        )}
        {isActive && (
          <Badge
            variant="secondary"
            className="shrink-0 bg-success/10 text-success border-success/20"
          >
            {t('admin.live_active_badge')}
          </Badge>
        )}
      </button>

      {open && (
        <div className="divide-y divide-border border-t border-border">
          {leads.length === 0 ? (
            <p className="px-10 py-4 text-sm text-muted-foreground italic">
              {t('admin.live_no_leads')}
            </p>
          ) : (
            sortedLeads.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                open={openLeads.has(lead.id)}
                onToggle={() => onToggleLead(lead.id)}
                isActiveLead={isActive && lead.id === resolvedActiveLeadId}
                isActiveInvestigation={isActive}
                onSetActiveLead={onSetActiveLead}
                investigationId={investigation.id}
                activeFactId={activeFactId}
                activeLeadId={resolvedActiveLeadId}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}
